import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { getPatientProfileApi, type PatientProfile } from "@/portals/patient/api/patientProfileApi";
import { usePatientAuthStore } from "@/stores/patientAuthStore";
import { useProxiedImage } from "@/shared/hooks/useProxiedImage";
import type { UserProfile } from "@/shared/types";

interface PatientProfileContextValue {
  profile: PatientProfile;
  setProfile: (profile: PatientProfile) => void;
  refreshProfile: () => Promise<void>;
  // Fetched ONCE here (not per-component) — every consumer that needs the patient's
  // avatar reads this instead of independently calling useProxiedImage(profile.avatarUrl),
  // so 3 render locations no longer trigger 3 separate network fetches that resolve
  // out of sync with each other.
  avatarSrc: string;
}

const PatientProfileContext = createContext<PatientProfileContextValue | null>(null);

// Only firstName/lastName come from the real logged-in user (via authUser.name) at this
// point — gender/dateOfBirth/memberSince aren't part of the login response, so seeding them
// from the sample fallbackProfile would flash demo data (e.g. "Female · Age 40 · Jun 1985")
// before the real fetch below lands and blanks them out for an actual new account.
function toPatientProfile(fallback: UserProfile, email?: string): PatientProfile {
  return {
    id: fallback.id,
    firstName: fallback.firstName,
    lastName: fallback.lastName,
    email: email ?? "",
    pendingEmail: null,
    address: "",
    phone: "",
    phoneVerified: false,
    gender: null,
    dateOfBirth: null,
    memberSince: "",
    avatarKey: undefined,
    avatarUrl: undefined,
  };
}

export function PatientProfileProvider({
  fallbackProfile,
  children,
}: {
  fallbackProfile: UserProfile;
  children: React.ReactNode;
}) {
  const authUser = usePatientAuthStore((s) => s.user);
  const [profile, setProfile] = useState<PatientProfile>(() => {
    const [firstName, ...lastNameParts] = (authUser?.name ?? "").trim().split(/\s+/).filter(Boolean);
    return toPatientProfile({
      ...fallbackProfile,
      firstName: firstName || fallbackProfile.firstName,
      lastName: lastNameParts.join(" ") || fallbackProfile.lastName,
    }, authUser?.email);
  });

  useEffect(() => {
    if (!authUser?.name) return;
    const [firstName, ...lastNameParts] = authUser.name.trim().split(/\s+/).filter(Boolean);
    if (!firstName) return;
    setProfile((current) => ({
      ...current,
      firstName: current.firstName === fallbackProfile.firstName ? firstName : current.firstName,
      lastName: current.lastName === fallbackProfile.lastName ? lastNameParts.join(" ") || current.lastName : current.lastName,
      email: current.email || authUser.email,
    }));
  }, [authUser?.name, authUser?.email, fallbackProfile.firstName, fallbackProfile.lastName]);

  // Returns whether the fetch actually landed — used by the mount-time retry loop below.
  // Over a real network hop (e.g. an ngrok-tunneled backend) this single call is far more
  // likely to hit a transient failure than on localhost, and without a retry the profile
  // just stays stuck on the blank placeholder state forever instead of self-correcting.
  const fetchProfile = async (): Promise<boolean> => {
    const { data } = await getPatientProfileApi();
    if (data) setProfile(data);
    return Boolean(data);
  };

  const refreshProfile = async () => {
    await fetchProfile();
  };

  useEffect(() => {
    let cancelled = false;

    const attempt = async (retriesLeft: number) => {
      const succeeded = await fetchProfile();
      if (succeeded || cancelled || retriesLeft <= 0) return;
      await new Promise((resolve) => setTimeout(resolve, 1000));
      if (!cancelled) await attempt(retriesLeft - 1);
    };

    void attempt(2);
    return () => {
      cancelled = true;
    };
  }, []);

  const avatarSrc = useProxiedImage(profile.avatarUrl);

  const value = useMemo(() => ({ profile, setProfile, refreshProfile, avatarSrc }), [profile, avatarSrc]);

  return (
    <PatientProfileContext.Provider value={value}>
      {children}
    </PatientProfileContext.Provider>
  );
}

export function usePatientProfile() {
  const context = useContext(PatientProfileContext);
  if (!context) throw new Error("usePatientProfile must be used inside PatientProfileProvider");
  return context;
}
